import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { PAYMENTS_QUEUE_HREF, taskHref } from '@/features/tasks/taskHref'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type {
  MyTaskApprovalItem,
  MyTaskRecordItem,
  MyTaskRecordSectionKey,
  MyTaskSection,
} from '@/types/domain'

/* One section of «Мои задачи» (S-2b.1, S-2b.2): its title, the served count, a
 * row per item, and the remainder as plain text.
 *
 * Every word is composed HERE from the section key and the item's typed fields
 * (FR-2): the server sends numbers, names and instants, never a sentence. Each
 * kind of section has its own row, because the generic one would have to
 * invent a `since` or a hint it does not have:
 *
 *   · a RECORD row (the eight quote and case sections) is the number, the
 *     client and the day it has waited since — on `expiring-soon`, the day
 *     the offer stands until — linking to the record;
 *   · a RATE-APPROVAL row is the case number, the requester and the signed
 *     change, linking to the approvals tab — the reason stays there (AD-6);
 *   · `payments-to-confirm` has no rows at all: it is a count, and the card is
 *     one link to the confirmation queue.
 *
 * The rows deliberately carry nothing else — the per-kind item allowlist
 * (`TestAnonMyTasksItemCarriesOnlyItsDeclaredFields`) keeps carriers, plates,
 * reasons and free text off the wire, and the record's own page is one click
 * away.
 *
 * `limit` and `linkAllTasks` are the dashboard strip's: it shows five rows a
 * section, and a section with more says how many and links «Мои задачи»,
 * which lists them by the same rules. The page itself passes neither. */

interface MyTaskSectionCardProps {
  section: MyTaskSection
  /** At most this many rows; the rest are counted in the remainder. */
  limit?: number
  /** Follow the remainder with «Все задачи» → `/tasks` (the strip). */
  linkAllTasks?: boolean
}

export function MyTaskSectionCard({ section, limit, linkAllTasks }: MyTaskSectionCardProps) {
  if (section.key === 'payments-to-confirm') {
    return <PaymentsToConfirmCard count={section.count} />
  }

  if (section.key === 'rate-approvals') {
    const items = section.items.slice(0, limit)
    return (
      <RowsCard
        copy={`myTasks.sections.${section.key}`}
        count={section.count}
        shown={items.length}
        linkAllTasks={linkAllTasks}
      >
        {items.map((item, index) => (
          <TaskRow
            key={item.id}
            href={taskHref(item)}
            divided={index !== items.length - 1 || section.count > items.length}
          >
            <ApprovalRow item={item} />
          </TaskRow>
        ))}
      </RowsCard>
    )
  }

  const sectionKey = section.key
  const items = section.items.slice(0, limit)
  return (
    <RowsCard
      copy={`myTasks.sections.${sectionKey}`}
      count={section.count}
      shown={items.length}
      linkAllTasks={linkAllTasks}
    >
      {items.map((item, index) => (
        <TaskRow
          key={item.legId ?? item.id}
          href={taskHref(item)}
          divided={index !== items.length - 1 || section.count > items.length}
        >
          <RecordRow item={item} sectionKey={sectionKey} />
        </TaskRow>
      ))}
    </RowsCard>
  )
}

/** The frame every listing section shares: the header, the rows or «Ничего не
 *  ждёт», and the remainder. */
function RowsCard({
  copy,
  count,
  shown,
  linkAllTasks,
  children,
}: {
  copy: `myTasks.sections.${MyTaskRecordSectionKey | 'rate-approvals'}`
  /** The real total; `shown` is how many rows are drawn. */
  count: number
  shown: number
  linkAllTasks?: boolean
  children: ReactNode
}) {
  const { t } = useTranslation()
  /* `count` is the real total; the rows stop at 50 on the wire (at five in the
     strip, and lose any item of a kind this build cannot link to). The
     remainder is what the rows do not show. */
  const remainder = count - shown

  return (
    <Card>
      <CardHeader
        action={<span className="asa-num text-text-muted text-[13px] font-normal">{count}</span>}
      >
        <CardTitle>{t(`${copy}.title`)}</CardTitle>
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t(`${copy}.hint`)}
        </p>
      </CardHeader>

      {shown === 0 ? (
        /* A held section with nothing in it is an answer, not a failed load —
           stated plainly (UX-DR-17). */
        <p className="text-text-muted px-[18px] pt-3 pb-4 text-[13px] font-light">
          {t('myTasks.nothingWaiting')}
        </p>
      ) : (
        <ul className="mt-3 flex flex-col">{children}</ul>
      )}

      {remainder > 0 ? (
        /* Plain text on the page, not a «Показать все» link (QW-9): the quote
           and case lists cannot filter by these rules, so the list behind a
           link would disagree with the count beside it. The strip's link goes
           to «Мои задачи», which lists by the same rules. */
        <p className="text-text-muted flex flex-wrap items-baseline gap-x-3 px-[18px] py-2.5 text-[12.5px] font-light">
          <span>{t('myTasks.more', { count: remainder })}</span>
          {linkAllTasks ? (
            <Link to="/tasks" className="text-[12.5px] font-normal">
              {t('myTasks.allTasks')}
            </Link>
          ) : null}
        </p>
      ) : null}
    </Card>
  )
}

function TaskRow({
  href,
  divided,
  children,
}: {
  href: string
  divided: boolean
  children: ReactNode
}) {
  return (
    <li>
      <Link
        to={href}
        className={cn(
          'flex items-center gap-3 px-[18px] py-2.5 no-underline',
          'hover:bg-surface-hover transition-colors',
          'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
          'hover:no-underline',
          divided && 'border-line-hair border-b',
        )}
      >
        {children}
      </Link>
    </li>
  )
}

/** A quote or a case: its number, its client, and since when it has waited —
 *  or, on `expiring-soon`, until when the offer stands. */
function RecordRow({
  item,
  sectionKey,
}: {
  item: MyTaskRecordItem
  sectionKey: MyTaskRecordSectionKey
}) {
  const { t } = useTranslation()
  const format = useFormat()
  return (
    <>
      {/* A record identifier: one of the four sanctioned uses of weight 600. */}
      <span className="text-brand w-[104px] shrink-0 truncate text-[13px] font-semibold">
        {item.number}
      </span>
      <span className="text-text-heading min-w-0 flex-1 truncate text-[13px] font-normal">
        {item.clientName}
      </span>
      {sectionKey === 'expiring-soon' ? (
        /* The END of the wait, not its start: a date-only `validUntil`
           (AD-19), so `format.date` — read as an instant it is UTC midnight,
           and west of Greenwich the row would promise a day less. */
        item.validUntil ? (
          <span className="text-text-muted shrink-0 text-[12px] font-light whitespace-nowrap">
            {t('myTasks.sections.expiring-soon.until', { date: format.date(item.validUntil) })}
          </span>
        ) : null
      ) : item.since ? (
        <span className="text-text-muted shrink-0 text-[12px] font-light whitespace-nowrap">
          {t(`myTasks.sections.${sectionKey}.since`, { date: format.instantDate(item.since) })}
        </span>
      ) : null}
    </>
  )
}

/** A rate-change request: the case, who asked, and the change it asks for.
 *
 *  The change is SIGNED in text as well as in tone — a lower rate is margin
 *  given away, a raise is not — because colour never carries the meaning
 *  alone. It is one request's own figure in its own currency, as served;
 *  nothing is summed here (AD-9). Weight 400: a requested change is neither a
 *  KPI nor a computed total. */
function ApprovalRow({ item }: { item: MyTaskApprovalItem }) {
  const negative = item.amount.amount.startsWith('-')
  /* A comparison, not arithmetic: a zero change (which no request should
     carry) gets neither a sign nor a tone. */
  const positive = !negative && Number(item.amount.amount) > 0
  return (
    <>
      <span className="text-brand w-[104px] shrink-0 truncate text-[13px] font-semibold">
        {item.number}
      </span>
      <span className="text-text-heading min-w-0 flex-1 truncate text-[13px] font-normal">
        {item.requesterName}
      </span>
      <span
        className={cn(
          'shrink-0 text-[13px] font-normal whitespace-nowrap',
          negative ? 'text-negative' : positive ? 'text-positive' : 'text-text-heading',
        )}
      >
        {/* Only the digits are tabular; the code keeps to its figure. */}
        <span className="asa-num">
          {positive ? '+' : ''}
          {formatNumber(item.amount.amount, 2)}
        </span>
        <span className="text-text-muted ml-1">{item.amount.currency}</span>
      </span>
    </>
  )
}

/** The payment-confirmation queue as a count: no payer and no amount are on
 *  the wire, so there is no row to draw and no remainder to state. With
 *  something waiting the whole card opens the queue — the same all-time list
 *  the sidebar's Finance badge counts; at zero it says nothing waits and
 *  links nowhere. */
function PaymentsToConfirmCard({ count }: { count: number }) {
  const { t } = useTranslation()
  const copy = 'myTasks.sections.payments-to-confirm'

  const card = (
    <Card
      className={cn(
        count > 0 &&
          'group-hover:bg-surface-hover transition-colors duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
      )}
    >
      <CardHeader
        action={
          count > 0 ? (
            <ChevronRight size={16} className="text-text-muted" aria-hidden />
          ) : (
            <span className="asa-num text-text-muted text-[13px] font-normal">{count}</span>
          )
        }
      >
        <CardTitle>{t(`${copy}.title`)}</CardTitle>
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t(`${copy}.hint`)}
        </p>
      </CardHeader>
      <p
        className={cn(
          'px-[18px] pt-3 pb-4 text-[13px]',
          count > 0 ? 'text-text-heading font-normal' : 'text-text-muted font-light',
        )}
      >
        {count > 0 ? t(`${copy}.awaiting`, { count }) : t('myTasks.nothingWaiting')}
      </p>
    </Card>
  )

  if (count === 0) return card
  return (
    <Link to={PAYMENTS_QUEUE_HREF} className="group block no-underline hover:no-underline">
      {card}
    </Link>
  )
}
